import {computed} from 'vue'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {PlanFeatureType} from '@toolify/server/src/models/mongoose/PlanModel/enum/PlanFeatureType'
import {FeatureFlagType} from '@toolify/server/src/models/mongoose/PlanModel/enum/FeatureFlagType'
import {PolicyLimitType} from '@toolify/server/src/models/mongoose/PlanModel/enum/PolicyLimitType'
import {UsageType} from '@toolify/server/src/models/mongoose/PlanModel/enum/UsageType'
import {CurrencyType} from '@toolify/server/src/models/mongoose/PlanPricingModel/enum/CurrencyType'
import {deriveWorkspaceEntitlementsFromPlanFeatures} from '@toolify/server/src/models/mongoose/PlanModel/planFeatureDerivation'
import {getPlanCapPermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/planCapPermissionTypes'

export function useCurrentWorkspacePlan() {
  const workspaceStore = useWorkspaceStore()

  const planFeatures = computed(() => {
    return workspaceStore.currentWorkspace?.planFeatures ?? []
  })

  function hasFeatureFlag(featureFlagType: FeatureFlagType): boolean {
    const state = new Map<FeatureFlagType, boolean>()
    for (const f of planFeatures.value) {
      if (f.planFeatureType === PlanFeatureType.FeatureFlag) {
        state.set(f.payload.featureFlagType, f.payload.isEnabled)
      }
    }
    if (!state.size) {
      return true
    }
    return state.get(featureFlagType) ?? false
  }

  function hasSeatForFeature(featureFlagType: FeatureFlagType): boolean {
    const planCapPermissionType = getPlanCapPermissionType(featureFlagType)
    if (!planFeatures.value.length || !planCapPermissionType) {
      return true
    }
    return workspaceStore.hasPermission(planCapPermissionType)
  }

  function isSeatMissingForAll(featureFlagTypes: FeatureFlagType[]): boolean {
    const includedFeatureFlagTypes = featureFlagTypes.filter(hasFeatureFlag)
    return includedFeatureFlagTypes.length > 0 && !includedFeatureFlagTypes.some(hasSeatForFeature)
  }

  function getPolicyLimit(policyLimitType: PolicyLimitType): number {
    const {policyLimits} = deriveWorkspaceEntitlementsFromPlanFeatures(planFeatures.value)
    return policyLimits[policyLimitType]
  }

  function getIncludedUsageLimit(usageType: UsageType): number {
    const {usageLimits} = deriveWorkspaceEntitlementsFromPlanFeatures(planFeatures.value)
    return usageLimits[usageType]
  }

  function getNotIncludedUsagePrice(usageType: UsageType): {currencyType: CurrencyType, amount: number} {
    let last: {currencyType: CurrencyType, amount: number} | undefined
    for (const f of planFeatures.value) {
      if (f.planFeatureType === PlanFeatureType.NotIncludedUsagePrice && f.payload.usageType === usageType) {
        last = {
          currencyType: f.payload.currencyType,
          amount: f.payload.amount,
        }
      }
    }
    return last ?? {
      currencyType: CurrencyType.USD,
      amount: 0,
    }
  }

  return {
    hasFeatureFlag,
    hasSeatForFeature,
    isSeatMissingForAll,
    getPolicyLimit,
    getIncludedUsageLimit,
    getNotIncludedUsagePrice,
  }
}
